import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Modal, 
  Input, 
  message, 
  Typography, 
  Space, 
  Avatar, 
  Tag, 
  Divider,
  Popconfirm,
  Dropdown,
  Menu,
  Badge
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  PlusOutlined, 
  MoreOutlined,
  SearchOutlined,
  StarOutlined,
  GoldOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;
const { Search } = Input;

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageName, setPackageName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    const filtered = packages.filter(pkg => 
      pkg.PackageName.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredPackages(filtered);
  }, [searchText, packages]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/packages`);
      setPackages(res.data);
      setFilteredPackages(res.data);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  };
  const getPackageColor = (name) => {
    if (!name) return { 
      bg: '#f5f7fa', 
      text: '#2c3e50',
      icon: <StarOutlined style={{ fontSize: 24 }} />
    };
    
    const lower = name.toLowerCase();
    
    // Premium package - Gold gradient with crown icon
    if (lower.includes('premium')) return { 
      bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', 
      text: '#fff',
      icon: <CrownOutlined style={{ fontSize: 24 }} />
    };
    
    // Standard package - Blue gradient with star icon
    if (lower.includes('standard')) return { 
      bg: 'linear-gradient(135deg, #4169E1 0%, #1E90FF 100%)', 
      text: '#fff',
      icon: <StarOutlined style={{ fontSize: 24 }} />
    };
    
    // Basic package - Green gradient with simple icon
    if (lower.includes('basic')) return { 
      bg: 'linear-gradient(135deg, #3CB371 0%, #2E8B57 100%)', 
      text: '#fff',
      icon: <StarOutlined style={{ fontSize: 24 }} />
    };
    
    // Default color for other packages - Neutral gradient
    return {
      bg: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)',
      text: '#fff',
      icon: <StarOutlined style={{ fontSize: 24 }} />
    };
    return {
      ...colors[index],
      icon: <StarOutlined style={{ fontSize: 24 }} />
    };
  };

  const showModal = (pkg = null) => {
    setSelectedPackage(pkg);
    setPackageName(pkg ? pkg.PackageName : '');
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    try {
      if (selectedPackage) {
        await axios.put(`${baseUrl}/packages/${selectedPackage.PackageID}`, { 
          PackageName: packageName 
        });
        message.success('Package updated successfully');
      } else {
        await axios.post(`${baseUrl}/packages`, { 
          PackageName: packageName 
        });
        message.success('Package created successfully');
      }
      setIsModalVisible(false);
      fetchPackages();
    } catch (error) {
      console.error(error);
      message.error(`Failed to ${selectedPackage ? 'update' : 'create'} package`);
    }
  };

  const handleDelete = async (pkg) => {
    try {
      await axios.delete(`${baseUrl}/packages/${pkg.PackageID}`);
      message.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error(error);
      message.error('Failed to delete package');
    }
  };

  const handleViewDetails = (packageId) => {
    // navigate(`/components?packageId=${packageId}`);
    navigate(`/components`, {
      state : {packageId : packageId}
    });
  };

  const moreMenu = (pkg) => (
    <Menu>
      <Menu.Item 
        icon={<EditOutlined />} 
        onClick={() => showModal(pkg)}
      >
        Edit Package
      </Menu.Item>
      <Menu.Item 
        icon={<EyeOutlined />}
        onClick={() => handleViewDetails(pkg.PackageID)}
      >
        View Package Details
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        icon={<DeleteOutlined />} 
        danger
        onClick={() => Modal.confirm({
          title: 'Delete Package',
          content: `Are you sure you want to delete ${pkg.PackageName}?`,
          okText: 'Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: () => handleDelete(pkg),
          maskStyle: { backdropFilter: 'blur(8px)' }
        })}
      >
        Delete
      </Menu.Item>
    </Menu>
  );

  return (
    <div >
      <Card 
        bordered={false}
        style={{ borderRadius: 8, boxShadow: 'none' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Packages</Title>
            <Text type="secondary">Manage your product packages</Text>
          </Col>
          <Col>
            <Space>
              <Search
                placeholder="Search packages..."
                allowClear
                enterButton={<SearchOutlined />}
                size="middle"
                onSearch={value => setSearchText(value)}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 250 }}
              />
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => showModal()}
              >
                New Package
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          {filteredPackages.map(pkg => {
            const { bg, text, icon } = getPackageColor(pkg.PackageName);
            
            return (
              <Col key={pkg.PackageID} xs={24} sm={12} md={8} lg={8}>                <Card
                  hoverable
                  style={{ 
                    background: bg,
                    color: text,
                    borderRadius: 8,
                    height: '180px',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ 
                    padding: '12px 16px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: 8
                  }}>
                    <Avatar 
                      style={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: text
                      }}
                      size={40}
                    >
                      {icon}
                    </Avatar>
                    <Dropdown overlay={moreMenu(pkg)} trigger={['click']}>
                      <Button 
                        type="text" 
                        icon={<MoreOutlined />} 
                        style={{ color: text }}
                      />
                    </Dropdown>
                  </div>
                    <Title 
                    level={4} 
                    style={{ 
                      margin: '4px 0',
                      color: text,
                      flex: 1,
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '1.4'
                    }}
                  >
                    {pkg.PackageName}
                  </Title>
                  
                  <Divider style={{ 
                    margin: '8px 0',
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }} />
                  
                  <Button 
                    block 
                    type="default" 
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(pkg.PackageID)}
                    style={{ 
                      background: 'rgba(255,255,255,0.2)',
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: text
                    }}
                  >
                    View Package Details
                  </Button>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* Package Modal */}
      <Modal
        title={selectedPackage ? `Edit ${selectedPackage.PackageName}` : 'Create New Package'}
        visible={isModalVisible}
        onOk={handleSave}
        onCancel={() => setIsModalVisible(false)}
        okText={selectedPackage ? 'Save Changes' : 'Create Package'}
        centered
        maskStyle={{ backdropFilter: 'blur(8px)' }}
      >
        <Input
          value={packageName}
          onChange={(e) => setPackageName(e.target.value)}
          placeholder="Enter package name"
          size="large"
          style={{ marginTop: 16 }}
        />
        {selectedPackage && (
          <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
            Package ID: {selectedPackage.PackageID}
          </Text>
        )}
      </Modal>
    </div>
  );
};

export default Packages;